import { useEffect, useState } from "react";
import { PaymentPreference } from "../components/PaymentPreference";
import { DeliveryOptions, MeetupArea } from "../components/DeliveryOptions";
import { useCheckoutConfig } from "../lib/checkout";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser } from "@clerk/react";
import { api } from "../lib/api";
import { useCart, money } from "../lib/cart";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Lock, AlertCircle } from "lucide-react";
import { readAck, ACK_VERSION } from "../lib/gate";
import { toast } from "sonner";

export default function Checkout() {
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const { items, subtotal_cents, clear, hasResearch } = useCart();
  const config = useCheckoutConfig();
  const [delivery, setDelivery] = useState("ship");
  const [meetupArea, setMeetupArea] = useState("");
  const meetup = delivery === "meetup" && !!config?.meetup_enabled;
  const shipping = config === null ? null : meetup ? 0 : config.shipping_cents;

  const [form, setForm] = useState({
    customer_name: user?.fullName || "",
    customer_email: user?.primaryEmailAddress?.emailAddress || "",
    customer_phone: "",
    line1: "", line2: "", city: "", state: "", zip: "", country: "US",
    attest: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [allMethods, setMethods] = useState([]);
  const [pref, setPref] = useState("");
  const [handle, setHandle] = useState("");
  const methods = allMethods.filter((m) => meetup || !m.meetup_only);
  const chosen = methods.find((m) => m.key === pref);

  useEffect(() => {
    if (pref && !methods.some((m) => m.key === pref)) setPref("");
  }, [pref, methods]);

  useEffect(() => {
    api.get("/payment-methods").then((r) => setMethods(r.data || [])).catch(() => setMethods([]));
  }, []);

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      customer_name: f.customer_name || user.fullName || "",
      customer_email: f.customer_email || user.primaryEmailAddress?.emailAddress || "",
    }));
  }, [user]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (!isLoaded) return <div className="p-20 text-center text-[#5E4D5A]">Loading…</div>;
  if (items.length === 0) {
    return <div className="max-w-3xl mx-auto px-6 py-20 text-center">
      <h1 className="font-serif text-3xl">Nothing to check out</h1>
      <button onClick={() => navigate("/shop")} className="mt-6 bg-[#59394F] text-white rounded-full px-6 py-3 text-sm">Browse catalog</button>
    </div>;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (meetup && form.customer_phone.replace(/\D/g, "").length < 7) return toast.error("Please enter a phone number so we can coordinate your local meetup.");
    if (meetup && !meetupArea) return toast.error("Please choose a meetup area: Enterprise, AL or Daleville, AL.");
    if (methods.length && !chosen) return toast.error("Please choose a payment preference.");
    if (chosen?.needs_handle && !handle.trim()) return toast.error(`Please enter your ${chosen.label} email or username.`);
    if (hasResearch && !form.attest) return toast.error("Please confirm the research-use attestation.");
    const ack = readAck() || { institution: "", policy_version: ACK_VERSION, acknowledged_at: new Date().toISOString() };
    setSubmitting(true);
    try {
      const headers = {};
      if (user) {
        const token = await getToken();
        if (token) headers.Authorization = `Bearer ${token}`;
      }
      const res = await api.post("/orders", {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone || null,
        delivery_method: meetup ? "meetup" : "ship",
        meetup_area: meetup ? meetupArea : null,
        shipping_address: meetup ? null : { line1: form.line1, line2: form.line2, city: form.city, state: form.state, zip: form.zip, country: form.country },
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, quantity: i.quantity })),
        payment_preference: chosen ? chosen.key : null,
        payment_handle: chosen?.needs_handle ? handle.trim() : null,
        attested: form.attest,
        researcher_ack: ack,
      }, { headers });
      clear();
      navigate("/order-received", { state: { order: res.data } });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16">
      <p className="text-xs uppercase tracking-[0.3em] text-[#5E6B51]">Checkout</p>
      <h1 className="font-serif text-5xl mt-2 mb-10">Almost there</h1>

      <div className="rounded-2xl bg-[#C99898]/10 border border-[#C99898]/30 p-5 mb-10 flex gap-4 items-start" data-testid="checkout-payment-notice">
        <AlertCircle size={20} className="text-[#59394F] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-[#5A433B]">No payment is collected on this website.</p>
          <p className="text-sm text-[#5E4D5A] mt-1 leading-relaxed">
            Submit your order and you'll receive an order number. Our team will contact you separately with payment details. Please don't send payment until you hear from us.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-[#A8B59A]/20 p-6">
            <h3 className="font-serif text-xl mb-5">Contact</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input data-testid="co-name" required value={form.customer_name} onChange={update("customer_name")} className="mt-2 rounded-xl" />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="co-email" required type="email" value={form.customer_email} onChange={update("customer_email")} className="mt-2 rounded-xl" />
              </div>
              <div className="sm:col-span-2">
                <Label>{meetup ? "Phone (required for local meetup)" : "Phone (optional)"}</Label>
                <Input data-testid="co-phone" type="tel" required={meetup} value={form.customer_phone} onChange={update("customer_phone")} className="mt-2 rounded-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#A8B59A]/20 p-6">
            <h3 className="font-serif text-xl mb-5">Delivery</h3>
            <DeliveryOptions value={meetup ? "meetup" : "ship"} onChange={setDelivery} meetupEnabled={!!config?.meetup_enabled} />
            {meetup && <MeetupArea value={meetupArea} onChange={setMeetupArea} />}
          </div>

          {!meetup && (
          <div className="bg-white rounded-2xl border border-[#A8B59A]/20 p-6" data-testid="shipping-address-card">
            <h3 className="font-serif text-xl mb-5">Shipping address</h3>
            <div className="grid gap-4">
              <div><Label>Address line 1</Label><Input data-testid="co-line1" required value={form.line1} onChange={update("line1")} className="mt-2 rounded-xl" /></div>
              <div><Label>Address line 2 (optional)</Label><Input data-testid="co-line2" value={form.line2} onChange={update("line2")} className="mt-2 rounded-xl" /></div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label>City</Label><Input data-testid="co-city" required value={form.city} onChange={update("city")} className="mt-2 rounded-xl" /></div>
                <div><Label>State</Label><Input data-testid="co-state" required value={form.state} onChange={update("state")} className="mt-2 rounded-xl" /></div>
                <div><Label>Zip</Label><Input data-testid="co-zip" required value={form.zip} onChange={update("zip")} className="mt-2 rounded-xl" /></div>
              </div>
            </div>
          </div>
          )}

          <div className="bg-white rounded-2xl border border-[#A8B59A]/20 p-6">
            <h3 className="font-serif text-xl mb-5">Payment preference</h3>
            <PaymentPreference methods={methods} value={pref} onChange={setPref} handle={handle} onHandle={setHandle} />
          </div>

          {hasResearch && (
          <div className="bg-white rounded-2xl border border-[#A8B59A]/20 p-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="co-attest" checked={form.attest} onCheckedChange={(v) => setForm((f) => ({ ...f, attest: !!v }))} className="mt-1" />
              <span className="text-sm text-[#5A433B] leading-relaxed">
                I confirm I am a qualified researcher, aged 21 or older, and these materials are purchased <strong>for research use only</strong>, not for human or animal consumption.
              </span>
            </label>
          </div>
          )}
        </div>

        <aside className="bg-white rounded-2xl border border-[#A8B59A]/20 p-6 h-fit">
          <h3 className="font-serif text-xl mb-4">Order summary</h3>
          <div className="space-y-3 mb-5">
            {items.map((it) => (
              <div key={it.key} className="flex justify-between text-sm">
                <span className="text-[#5E4D5A]">{it.name} × {it.quantity}</span>
                <span>{money(it.price_cents * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-[#A8B59A]/20 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="co-subtotal">{money(subtotal_cents)}</span></div>
            <div className="flex justify-between"><span>{meetup ? "Local meetup" : "Shipping (flat rate)"}</span><span data-testid="co-shipping">{shipping === null ? "…" : meetup ? "Free" : money(shipping)}</span></div>
            <div className="flex justify-between font-medium text-base pt-2 border-t border-[#A8B59A]/20"><span>Total</span><span data-testid="co-total">{money(subtotal_cents + (shipping || 0))}</span></div>
          </div>
          <Button type="submit" disabled={submitting} data-testid="co-submit" className="mt-6 w-full rounded-full bg-[#59394F] hover:bg-[#432A3B] text-white py-6 h-auto transition-colors">
            <Lock size={14} className="mr-2" /> {submitting ? "Submitting…" : "Submit order"}
          </Button>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#5E6B51] mt-4 text-center">Payment collected separately{hasResearch ? " · Research use only" : ""}</p>
        </aside>
      </form>
    </div>
  );
}
